import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Download, Info } from 'lucide-react';
import { scoreMovementDashboardAPI, type DashRichFilterParams } from '../../services/api';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import StatTile from './StatTile';
import { scoreMovementListFromSlug, type ScoreMovementList } from './lists/scoreMovement';
import { useDashboardList } from './useDashboardList';
import { useTableSort, SortTh } from './useTableSort';
import { useStoreRegions } from '../../hooks/useStoreRegions';
import {
  BAND_META,
  FALLING_STREAK_MIN,
  MOVE_META,
  RANKED_BANDS,
  TONE_TEXT,
  breakdownBy,
  changeAt,
  employeeCsv,
  fmtDelta,
  fmtScore,
  groupCsv,
  monthLabel,
  monthlyCsv,
  paramTrendOf,
  stillFalling,
  toneOf,
  topDecliners,
  topGainers,
  type BandKey,
  type BandTransition,
  type EmployeeMovement,
  type GroupSeries,
  type MonthPoint,
  type MoveKey,
  type ParamSeries,
  type ScoreMovementData,
  type Series,
} from './scoreMovement';

/**
 * Dashboard → Score Movement tab.
 *
 * The Employee Score page shows where each person stands in one month. This shows
 * the direction: six months of those same scores, who rose and who fell, how each
 * Assistant Manager's and Trade Head's team is trending, and which parameter is
 * behind it.
 *
 * Two things a reader must understand, both said in the UI rather than left here:
 *   1. Standing is the Employee Score page's own — each person ranked inside their
 *      manager's team. So every team holds about a quarter of its people in each
 *      band, and band counts do not rank one manager against another. Average score
 *      and the improved / declined counts do. The Standing view of the chart says so.
 *   2. "Improved" is a person against their OWN previous month, by a full point or
 *      more. The server decides that and stamps it per person per month; nothing on
 *      this tab re-derives it, so a card and the list behind it always agree.
 *
 * ONE request feeds everything. Every drill-down, sort, search and download is a
 * filter over the people it returned.
 */

// ── Small shared pieces ──────────────────────────────────────────────────────

function Card({ title, subtitle, action, children }: { title: string; subtitle?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      {/* The action stays on the title's row and the subtitle wraps beneath it. With
          flex-wrap, a long subtitle in a half-width card pushed the buttons onto a
          row of their own, left-aligned under the text. */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      {children}
    </section>
  );
}

const PILL = 'inline-flex items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50';

function ListActions({ onDownload, onViewAll }: { onDownload: () => void; onViewAll?: () => void }) {
  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      <button type="button" onClick={onDownload} className={PILL} title="Download the full list as CSV">
        <Download className="h-3.5 w-3.5" /> Download
      </button>
      {onViewAll && (
        <button type="button" onClick={onViewAll} className={PILL}>
          View All <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/** Two-or-more-way toggle, styled like the dashboard's own tab strip. */
function Segmented<T extends string>({ value, options, onChange }: {
  value: T; options: Array<{ key: T; label: string }>; onChange: (v: T) => void;
}) {
  return (
    <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          aria-pressed={value === o.key}
          className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
            value === o.key ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function BandChip({ band }: { band: BandKey | null }) {
  if (!band) return <span className="text-gray-300">—</span>;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${BAND_META[band].chip}`}>{BAND_META[band].label}</span>;
}

const TH = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const THR = 'px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TD_BASE = 'px-3 py-2 text-sm';
const TD = `${TD_BASE} text-gray-700`;
const TDR_BASE = 'px-2 py-2 text-right text-sm tabular-nums';
const TDR = `${TDR_BASE} text-gray-700`;
/**
 * A numeric cell in a colour other than the default grey.
 *
 * Deliberately NOT `${TDR} text-emerald-600`: two text-colour utilities on one element
 * are settled by their order in the generated stylesheet, not in the class list, and
 * here that order let rose win over the base grey while emerald lost to it — so every
 * decline showed red and every improvement stayed grey.
 */
const tdr = (color: string, extra = ''): string => `${TDR_BASE} ${color}${extra ? ` ${extra}` : ''}`;

/** Rows shown in a card before "View all" — the ten every other tab's lists cap at. */
const TOP_N = 10;
/** Watchlist cards are three to a row, so they show fewer. */
const WATCH_N = 5;
/** Rows a modal table renders at once. A whole project is ~2,500 people × 12 columns;
 *  past this the list says so and points at search and the download instead. */
const MODAL_ROW_CAP = 300;

// ── Sparkline ────────────────────────────────────────────────────────────────

/** A trend line with no axes — direction only. Coloured by first → last. */
function Sparkline({ values, width = 92, height = 24 }: { values: Array<number | null>; width?: number; height?: number }) {
  const pts = values.map((v, i) => ({ v, i })).filter((p): p is { v: number; i: number } => p.v != null);
  if (pts.length < 2) return <span className="text-[11px] text-gray-300">—</span>;
  let lo = Math.min(...pts.map((p) => p.v));
  let hi = Math.max(...pts.map((p) => p.v));
  // A near-flat series must draw as a flat line, not as noise stretched to full height.
  if (hi - lo < 2) { const mid = (hi + lo) / 2; lo = mid - 1; hi = mid + 1; }
  const pad = 3;
  const x = (i: number) => pad + (values.length > 1 ? (i / (values.length - 1)) * (width - pad * 2) : 0);
  const y = (v: number) => pad + ((hi - v) / (hi - lo)) * (height - pad * 2);
  const tone = toneOf(pts[pts.length - 1].v - pts[0].v, 0.5);
  const stroke = tone === 'good' ? '#10B981' : tone === 'bad' ? '#F43F5E' : '#9CA3AF';
  const end = pts[pts.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="inline-block align-middle" aria-hidden="true">
      <polyline points={pts.map((p) => `${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ')} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(end.i)} cy={y(end.v)} r="2.25" fill={stroke} />
    </svg>
  );
}

// ── The trend chart ──────────────────────────────────────────────────────────
// Hand-drawn SVG, like the Attrition chart: a fixed six-column stack whose one
// fiddly need — rounding the STACK rather than each segment — is a clipPath in raw
// SVG and a fight in a charting library.
//
// Two panels on one set of columns, deliberately NOT one chart with two y-axes: the
// average is a score out of 100 and the bars are a headcount, and overlaying them
// invites reading the line against the bars' scale.

type ChartMode = 'movement' | 'standing';
const CW = 720;
const CH = 318;
const PADX = 22;
const LINE_TOP = 32;
const LINE_BOTTOM = 92;
// The bars start low enough to leave room for a value label UNDER the lower of the
// two lines (average and median) as well as above the higher one.
const BAR_TOP = 140;
const BAR_BASE = 276;
const BAR_GAP = 28;
const AVG_HEX = '#2563EB';
/** The median: a dashed slate line with hollow dots. Not another hue — the bars below
 *  already use five, and a second saturated line read as one more category of them. */
const MEDIAN_HEX = '#475569';

// Bottom → top. Good news sits on top in both views.
const MOVE_STACK: readonly MoveKey[] = ['declined', 'steady', 'new', 'improved'];
const BAND_STACK: readonly BandKey[] = ['other', 'atRisk', 'strivers', 'achievers', 'stars'];
/** Segments pale enough that white text on them is unreadable. */
const DARK_INK = new Set<string>(['steady', 'new', 'other', 'strivers']);

const moveCount = (p: MonthPoint, k: MoveKey): number =>
  k === 'improved' ? p.improved : k === 'declined' ? p.declined : k === 'steady' ? p.steady : p.fresh;

interface Segment { key: string; label: string; hex: string; count: number }
const segmentsOf = (p: MonthPoint, mode: ChartMode): Segment[] =>
  mode === 'movement'
    ? MOVE_STACK.map((k) => ({ key: k, label: MOVE_META[k].label, hex: MOVE_META[k].hex, count: moveCount(p, k) }))
    : BAND_STACK.map((k) => ({ key: k, label: BAND_META[k].label, hex: BAND_META[k].hex, count: p.bands[k] }));

function TrendChart({ monthly, mode, onPick, chartId }: {
  monthly: MonthPoint[];
  mode: ChartMode;
  onPick: (monthIdx: number, key: string, label: string) => void;
  /** Unique per chart on the page — the stacks' clip paths are document-global ids. */
  chartId: string;
}) {
  const n = monthly.length;
  const innerW = CW - PADX * 2;
  const barW = Math.min((innerW - BAR_GAP * (n - 1)) / Math.max(n, 1), 64);
  const x0 = PADX + (innerW - (n * barW + (n - 1) * BAR_GAP)) / 2;
  const cx = (i: number) => x0 + i * (barW + BAR_GAP) + barW / 2;

  const maxScored = Math.max(...monthly.map((p) => p.scored), 1);
  // One scale for both lines, so the gap between average and median is to scale.
  const hasMedian = monthly.some((p) => p.median != null);
  const avgs = monthly.flatMap((p) => [p.avg, p.median]).filter((v): v is number => v != null);
  let lo = avgs.length ? Math.min(...avgs) : 0;
  let hi = avgs.length ? Math.max(...avgs) : 100;
  // At least a four-point span, so a 0.3-point wobble does not draw as a cliff.
  if (hi - lo < 4) { const mid = (hi + lo) / 2; lo = mid - 2; hi = mid + 2; }
  const yOf = (v: number) => LINE_TOP + ((hi - v) / (hi - lo)) * (LINE_BOTTOM - LINE_TOP);

  // A line breaks across a month with nobody scored rather than bridging it.
  const pathOf = (pick: (p: MonthPoint) => number | null | undefined): string => {
    let d = '';
    let pen = false;
    monthly.forEach((p, i) => {
      const v = pick(p);
      if (v == null) { pen = false; return; }
      d += `${pen ? 'L' : 'M'}${cx(i).toFixed(1)},${yOf(v).toFixed(1)} `;
      pen = true;
    });
    return d;
  };
  const path = pathOf((p) => p.avg);
  const medianPath = pathOf((p) => p.median);

  const legend = mode === 'movement'
    ? [...MOVE_STACK].reverse().map((k) => ({ key: k as string, label: MOVE_META[k].label, hex: MOVE_META[k].hex }))
    : [...BAND_STACK].reverse().map((k) => ({ key: k as string, label: BAND_META[k].label, hex: BAND_META[k].hex }));
  const used = new Set<string>();
  for (const p of monthly) for (const s of segmentsOf(p, mode)) if (s.count > 0) used.add(s.key);

  return (
    <>
      <svg viewBox={`0 0 ${CW} ${CH}`} className="w-full" role="img" aria-label={`Average${hasMedian ? ' and median' : ''} score and employee movement by month`}>
        <text x={PADX} y={14} fill="#6B7280" fontSize="9.5" fontWeight="600">{hasMedian ? 'AVERAGE AND MEDIAN SCORE' : 'AVERAGE SCORE'}</text>
        <text x={PADX} y={BAR_TOP - 12} fill="#6B7280" fontSize="9.5" fontWeight="600">
          {mode === 'movement' ? 'EMPLOYEES, BY CHANGE ON THE MONTH BEFORE' : 'EMPLOYEES, BY STANDING'}
        </text>
        <line x1={PADX} x2={CW - PADX} y1={BAR_BASE} y2={BAR_BASE} stroke="#E5E7EB" strokeWidth="0.75" />

        {/* Average first, median OVER it. Painted the other way round, a team of one or
            two people — whose median IS its average — showed one solid line, and the
            legend offered a dashed one that was nowhere on the chart. */}
        {path && <path d={path} fill="none" stroke={AVG_HEX} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {medianPath && <path data-testid="median-line" d={medianPath} fill="none" stroke={MEDIAN_HEX} strokeWidth="1.5" strokeDasharray="4 3" strokeLinejoin="round" strokeLinecap="round" />}

        {monthly.map((p, i) => {
          const x = cx(i) - barW / 2;
          const barH = (p.scored / maxScored) * (BAR_BASE - BAR_TOP);
          const clipId = `${chartId}-stack-${p.month}`;
          let segBottom = BAR_BASE;
          return (
            <g key={p.month}>
              {/* Two values per month, never on top of each other: whichever is HIGHER
                  is labelled above its dot and the other below its own. When both would
                  PRINT the same figure — always so for a team of one or two — it is
                  printed once, and the median's ring is drawn around the average's dot
                  rather than hidden beneath it. */}
              {p.avg != null && (() => {
                const below = p.median != null && p.median > p.avg && p.median.toFixed(1) !== p.avg.toFixed(1);
                return (
                  <>
                    <circle data-testid={`avg-dot-${p.month}`} cx={cx(i)} cy={yOf(p.avg)} r="3.5" fill={AVG_HEX} stroke="#FFFFFF" strokeWidth="1.5">
                      <title>{`${monthLabel(p.month, 'long')}: average ${p.avg.toFixed(2)}${p.avgDelta != null ? ` (${fmtDelta(p.avgDelta, 2)} on the month before)` : ''}`}</title>
                    </circle>
                    <text data-testid={`avg-label-${p.month}`} x={cx(i)} y={below ? yOf(p.avg) + 14 : yOf(p.avg) - 8} textAnchor="middle" fill="#1E3A8A" fontSize="10" fontWeight="700">
                      {p.avg.toFixed(1)}
                    </text>
                  </>
                );
              })()}
              {p.median != null && (() => {
                const sameFigure = p.avg != null && p.median.toFixed(1) === p.avg.toFixed(1);
                // Close enough to sit on the average's dot: an open ring around it.
                const onAverage = p.avg != null && Math.abs(yOf(p.median) - yOf(p.avg)) < 3;
                const below = p.avg == null || p.median <= p.avg;
                return (
                  <>
                    <circle
                      data-testid={`median-dot-${p.month}`}
                      cx={cx(i)}
                      cy={yOf(p.median)}
                      r={onAverage ? 5.5 : 3}
                      fill={onAverage ? 'none' : '#FFFFFF'}
                      stroke={MEDIAN_HEX}
                      strokeWidth="1.5"
                    >
                      <title>{`${monthLabel(p.month, 'long')}: median ${p.median.toFixed(2)}${p.medianDelta != null ? ` (${fmtDelta(p.medianDelta, 2)} on the month before)` : ''}`}</title>
                    </circle>
                    {!sameFigure && (
                      <text data-testid={`median-label-${p.month}`} x={cx(i)} y={below ? yOf(p.median) + 14 : yOf(p.median) - 8} textAnchor="middle" fill="#334155" fontSize="9.5" fontWeight="600">
                        {p.median.toFixed(1)}
                      </text>
                    )}
                  </>
                );
              })()}

              <clipPath id={clipId}>
                <rect x={x} y={BAR_BASE - barH} width={barW} height={Math.max(barH, 0)} rx={5} />
              </clipPath>
              <g clipPath={`url(#${clipId})`}>
                {segmentsOf(p, mode).map((s) => {
                  if (s.count <= 0 || p.scored <= 0) return null;
                  const segH = barH * (s.count / p.scored);
                  segBottom -= segH;
                  const y = segBottom;
                  return (
                    <g
                      key={s.key}
                      className="cursor-pointer"
                      data-testid={`seg-${p.month}-${s.key}`}
                      onClick={() => onPick(i, s.key, s.label)}
                    >
                      <rect x={x} y={y} width={barW} height={segH} fill={s.hex}>
                        <title>{`${monthLabel(p.month, 'long')} — ${s.label}: ${s.count} of ${p.scored} (${((s.count / p.scored) * 100).toFixed(1)}%)`}</title>
                      </rect>
                      {segH >= 13 && (
                        <text x={cx(i)} y={y + segH / 2 + 3.5} textAnchor="middle" fill={DARK_INK.has(s.key) ? '#334155' : '#FFFFFF'} fontSize="9" fontWeight="700" pointerEvents="none">
                          {s.count}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>

              <text x={cx(i)} y={BAR_BASE + 15} textAnchor="middle" fill="#374151" fontSize="10.5" fontWeight="600">
                {monthLabel(p.month)}
              </text>
              <text x={cx(i)} y={BAR_BASE + 28} textAnchor="middle" fill="#9CA3AF" fontSize="9">
                {p.scored > 0 ? `${p.scored.toLocaleString()} scored` : 'not scored'}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-2 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded bg-blue-600" />
          Average score
        </span>
        {hasMedian && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 border-t-2 border-dashed border-slate-600" />
            Median score
          </span>
        )}
        {legend.filter((l) => used.has(l.key)).map((l) => (
          <span key={l.key} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: l.hex }} />
            {l.label}
          </span>
        ))}
      </div>
    </>
  );
}

// ── Band → band ──────────────────────────────────────────────────────────────

const BAND_RANK: Record<BandKey, number | null> = { stars: 4, achievers: 3, strivers: 2, atRisk: 1, other: null };

function TransitionGrid({ transitions, fromLabel, toLabel, onPick }: {
  transitions: BandTransition[];
  fromLabel: string;
  toLabel: string;
  onPick: (from: BandKey | 'new', to: BandKey) => void;
}) {
  const count = (from: BandKey | 'new', to: BandKey) => transitions.find((t) => t.from === from && t.to === to)?.count ?? 0;
  const hasOther = transitions.some((t) => t.from === 'other' || t.to === 'other');
  const hasNew = transitions.some((t) => t.from === 'new');
  const cols: BandKey[] = hasOther ? [...RANKED_BANDS, 'other'] : [...RANKED_BANDS];
  const rows: Array<BandKey | 'new'> = [...RANKED_BANDS, ...(hasOther ? (['other'] as const) : []), ...(hasNew ? (['new'] as const) : [])];
  const max = Math.max(...transitions.map((t) => t.count), 1);
  const rowLabel = (r: BandKey | 'new') => (r === 'new' ? 'Not scored' : BAND_META[r].label);

  // rgb triples; the alpha carries the count.
  const tint = (from: BandKey | 'new', to: BandKey): string => {
    const f = from === 'new' ? null : BAND_RANK[from];
    const t = BAND_RANK[to];
    if (f == null || t == null) return '59,130,246'; // not a rank on one side — neutral blue
    return t > f ? '16,185,129' : t < f ? '244,63,94' : '100,116,139';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] border-separate border-spacing-1 text-sm">
        <thead>
          <tr>
            <th className="px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {fromLabel} ↓ &nbsp;/&nbsp; {toLabel} →
            </th>
            {cols.map((c) => (
              <th key={c} className="px-1 text-center text-[11px] font-semibold text-gray-600">{BAND_META[c].label}</th>
            ))}
            <th className="px-1 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const total = cols.reduce((sum, c) => sum + count(r, c), 0);
            return (
              <tr key={r}>
                <th scope="row" className="whitespace-nowrap px-1 text-left text-xs font-medium text-gray-700">{rowLabel(r)}</th>
                {cols.map((c) => {
                  const v = count(r, c);
                  if (v === 0) return <td key={c} className="rounded-md bg-gray-50 py-2 text-center text-xs text-gray-300">·</td>;
                  return (
                    <td key={c} className="p-0">
                      <button
                        type="button"
                        onClick={() => onPick(r, c)}
                        className="w-full rounded-md py-2 text-center text-sm font-semibold tabular-nums text-gray-900 transition hover:ring-2 hover:ring-primary-300"
                        style={{ backgroundColor: `rgba(${tint(r, c)},${(0.12 + 0.48 * (v / max)).toFixed(2)})` }}
                        title={`${rowLabel(r)} in ${fromLabel} → ${BAND_META[c].label} in ${toLabel}: ${v}`}
                        aria-label={`${rowLabel(r)} to ${BAND_META[c].label}: ${v}`}
                      >
                        {v.toLocaleString()}
                      </button>
                    </td>
                  );
                })}
                <td className="px-1 text-right text-xs tabular-nums text-gray-500">{total.toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" />Moved up</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-400" />Stayed</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-400" />Moved down</span>
      </div>
    </div>
  );
}

// ── Teams: who moved most ────────────────────────────────────────────────────

/** How many teams each side of the diverging bars shows. */
const TEAM_MOVERS_N = 5;

function TeamMoveBars({ groups, onPick }: { groups: GroupSeries[]; onPick: (g: GroupSeries) => void }) {
  const withDelta = groups
    .map((g) => ({ g, delta: g.monthly[g.monthly.length - 1]?.sameDelta ?? null }))
    .filter((r): r is { g: GroupSeries; delta: number } => r.delta != null && r.g.key !== null);
  const up = withDelta.filter((r) => r.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, TEAM_MOVERS_N);
  const down = withDelta.filter((r) => r.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, TEAM_MOVERS_N);
  const rows = [...up, ...down.reverse()];
  if (rows.length === 0) return <p className="py-6 text-center text-sm text-gray-500">No team has two months to compare yet.</p>;
  const max = Math.max(...rows.map((r) => Math.abs(r.delta)), 0.1);
  return (
    <div className="space-y-1.5">
      {rows.map(({ g, delta }) => {
        const w = `${Math.max((Math.abs(delta) / max) * 100, 2)}%`;
        return (
          <button
            key={g.key}
            type="button"
            onClick={() => onPick(g)}
            className="flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left hover:bg-gray-50"
            title={`${g.name}: ${fmtDelta(delta, 2)} points, team of ${g.teamSize}`}
          >
            <span className="w-36 shrink-0 truncate text-xs text-gray-700">{g.name}</span>
            <span className="flex h-3 flex-1 justify-end">
              {delta < 0 && <span className="h-full rounded-l bg-rose-400" style={{ width: w }} />}
            </span>
            <span className="h-4 w-px shrink-0 bg-gray-300" />
            <span className="flex h-3 flex-1">
              {delta > 0 && <span className="h-full rounded-r bg-emerald-400" style={{ width: w }} />}
            </span>
            <span className={`w-12 shrink-0 text-right text-xs font-semibold tabular-nums ${TONE_TEXT[toneOf(delta)]}`}>{fmtDelta(delta)}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Parameters ───────────────────────────────────────────────────────────────

const DIRECTION_META = {
  improving: { label: 'Improving', cls: 'bg-emerald-50 text-emerald-700' },
  declining: { label: 'Declining', cls: 'bg-rose-50 text-rose-700' },
  steady: { label: 'Steady', cls: 'bg-gray-100 text-gray-600' },
  none: { label: 'One month so far', cls: 'bg-gray-100 text-gray-500' },
} as const;

// A parameter's tile is drawn as the dashboards' headline tiles are (StatTile): the shared
// white frame, a grey label, one bold number and a short grey line. It keeps its own
// padding, its direction chip and its sparkline.
const PARAM_TILE = 'rounded-2xl border border-gray-200 bg-white p-3 shadow-sm';

function ParameterCards({ params, months, minDelta }: { params: ParamSeries[]; months: string[]; minDelta: number }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {params.map((p) => {
        const t = paramTrendOf(p, minDelta);
        const dir = DIRECTION_META[t.direction];
        return (
          <div key={p.code} className={PARAM_TILE}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 truncate text-sm font-medium text-gray-500" title={p.name}>{p.name}</div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${dir.cls}`}>{dir.label}</span>
            </div>
            <div className="mt-2 flex items-end justify-between gap-2">
              <div>
                <div className="text-3xl font-bold tabular-nums tracking-tight text-gray-900">{t.latest == null ? '—' : `${t.latest.toFixed(1)}%`}</div>
                <div className="text-xs text-gray-400">
                  {t.points != null && t.weight != null ? `${t.points.toFixed(1)} of ${t.weight.toFixed(0)} points` : 'no score yet'}
                  {t.latestIdx >= 0 && t.latestIdx !== months.length - 1 ? ` · ${monthLabel(months[t.latestIdx])}` : ''}
                </div>
              </div>
              <div className="text-right">
                <Sparkline values={p.pct} width={110} height={30} />
                <div className={`text-[11px] font-medium ${TONE_TEXT[toneOf(t.change)]}`}>
                  {t.change == null ? '' : `${fmtDelta(t.change)} pts vs ${monthLabel(months[t.previousIdx], 'short')}`}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Parameter × month, for one team's drill-down. */
function ParameterTable({ params, months, parameterMonths, minDelta }: {
  params: ParamSeries[]; months: string[]; parameterMonths: string[]; minDelta: number;
}) {
  const has = new Set(parameterMonths);
  const cols = months.map((m, i) => ({ m, i })).filter((c) => has.has(c.m));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className={TH}>Parameter</th>
            {cols.map((c) => <th key={c.m} className={THR}>{monthLabel(c.m)}</th>)}
            <th className={THR}>Change</th>
            <th className={TH}>Direction</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {params.map((p) => {
            const t = paramTrendOf(p, minDelta);
            return (
              <tr key={p.code}>
                <td className={`${TD_BASE} font-medium text-gray-900`}>{p.name}</td>
                {cols.map((c) => <td key={c.m} className={TDR}>{p.pct[c.i] == null ? '—' : `${p.pct[c.i]!.toFixed(1)}%`}</td>)}
                <td className={tdr(TONE_TEXT[toneOf(t.change)], 'font-medium')}>{fmtDelta(t.change)}</td>
                <td className={TD}>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${DIRECTION_META[t.direction].cls}`}>{DIRECTION_META[t.direction].label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Tables ───────────────────────────────────────────────────────────────────

/**
 * One row per Assistant Manager / Trade Head / reporting manager. Sorts the WHOLE
 * list and then caps it, so the card's ten are the ten biggest movers rather than
 * the first ten in the alphabet re-ordered.
 */
function GroupTable({ rows, months, nameLabel, limit, onRowClick }: {
  rows: GroupSeries[]; months: string[]; nameLabel: string; limit?: number; onRowClick: (g: GroupSeries) => void;
}) {
  const last = months.length - 1;
  const at = (g: GroupSeries): MonthPoint | undefined => g.monthly[last];
  const sort = useTableSort(rows, {
    name: (g: GroupSeries) => g.name,
    team: (g: GroupSeries) => g.teamSize,
    avg: (g: GroupSeries) => at(g)?.avg,
    median: (g: GroupSeries) => at(g)?.median,
    delta: (g: GroupSeries) => at(g)?.sameDelta,
    improved: (g: GroupSeries) => at(g)?.improved,
    declined: (g: GroupSeries) => at(g)?.declined,
    stars: (g: GroupSeries) => at(g)?.bands.stars,
    achievers: (g: GroupSeries) => at(g)?.bands.achievers,
    strivers: (g: GroupSeries) => at(g)?.bands.strivers,
    atRisk: (g: GroupSeries) => at(g)?.bands.atRisk,
  }, { defaultKey: 'delta', defaultDir: 'desc' });
  const shown = limit ? sort.sorted.slice(0, limit) : sort.sorted;
  const prevLabel = last >= 1 ? monthLabel(months[last - 1], 'short') : 'previous month';
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <SortTh label={nameLabel} sortKey="name" sort={sort} className={TH} />
            <SortTh label="Team" sortKey="team" sort={sort} align="right" className={THR} title="People scored in the latest month" />
            <th className={`${TH} whitespace-nowrap`} title="The team's average score over the six months. The median for each month is in the team's own chart — click the row.">Avg, 6 months</th>
            <SortTh label="Avg score" sortKey="avg" sort={sort} align="right" className={THR} />
            <SortTh label="Median" sortKey="median" sort={sort} align="right" className={THR} title="The team's middle score: half its people are at or above it. A few very low scores move the average but not this." />
            <SortTh label="Avg change" sortKey="delta" sort={sort} align="right" className={THR} title={`Average change in score since ${prevLabel} for people scored in both months. A joiner or a leaver cannot move it, so it can differ from the difference between two months' averages.`} />
            <SortTh label="Improved" sortKey="improved" sort={sort} align="right" className={THR} title="People whose own score rose by a point or more" />
            <SortTh label="Declined" sortKey="declined" sort={sort} align="right" className={THR} title="People whose own score fell by a point or more" />
            <SortTh label="Stars" sortKey="stars" sort={sort} align="right" className={THR} />
            <SortTh label="Achievers" sortKey="achievers" sort={sort} align="right" className={THR} />
            <SortTh label="Strivers" sortKey="strivers" sort={sort} align="right" className={THR} />
            <SortTh label="At Risk" sortKey="atRisk" sort={sort} align="right" className={THR} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {shown.map((g) => {
            const p = at(g);
            return (
              <tr key={g.key ?? '__none__'} className="cursor-pointer hover:bg-gray-50" onClick={() => onRowClick(g)}>
                <td className={TD}>
                  <div className="font-medium text-gray-900">{g.name}</div>
                  {g.key && <div className="text-[11px] text-gray-400">{g.key}</div>}
                </td>
                <td className={TDR}>{g.teamSize}</td>
                <td className={TD}><Sparkline values={g.monthly.map((m) => m.avg)} /></td>
                <td className={tdr('text-gray-900', 'font-medium')}>{fmtScore(p?.avg)}</td>
                <td className={tdr('text-gray-900', 'font-medium')}>{fmtScore(p?.median)}</td>
                <td className={tdr(TONE_TEXT[toneOf(p?.sameDelta)], 'font-medium')}>{fmtDelta(p?.sameDelta)}</td>
                <td className={tdr(p && p.improved > 0 ? 'text-emerald-700' : 'text-gray-700')}>{p?.improved ?? 0}</td>
                <td className={tdr(p && p.declined > 0 ? 'text-rose-700' : 'text-gray-700')}>{p?.declined ?? 0}</td>
                <td className={TDR}>{p?.bands.stars ?? 0}</td>
                <td className={TDR}>{p?.bands.achievers ?? 0}</td>
                <td className={TDR}>{p?.bands.strivers ?? 0}</td>
                <td className={TDR}>{p?.bands.atRisk ?? 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * `at` is the month the list is ABOUT — the latest by default, or the month whose bar
 * or summary row was clicked. Change and Standing are shown for THAT month, and its
 * score column is the emphasised one. Without it a list titled "Improved — Jun" showed
 * each person's latest-month change, so someone who rose in June and fell in August
 * appeared with a red minus under an "Improved" heading.
 */
function EmployeeTable({ rows, months, at: atProp }: { rows: EmployeeMovement[]; months: string[]; at?: number }) {
  const last = months.length - 1;
  const at = atProp ?? last;
  const monthAccessors = Object.fromEntries(months.map((_month, i) => [`m${i}`, (e: EmployeeMovement) => e.totals[i]]));
  const sort = useTableSort(rows, {
    emp: (e: EmployeeMovement) => e.name || e.code,
    store: (e: EmployeeMovement) => e.store,
    manager: (e: EmployeeMovement) => e.managerName,
    delta: (e: EmployeeMovement) => changeAt(e, at),
    ...monthAccessors,
  });
  if (rows.length === 0) return <p className="py-8 text-center text-sm text-gray-500">Nobody matches.</p>;
  const shown = sort.sorted.slice(0, MODAL_ROW_CAP);
  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <SortTh label="Employee" sortKey="emp" sort={sort} className={TH} />
              <SortTh label="Store" sortKey="store" sort={sort} className={TH} />
              <SortTh label="Manager" sortKey="manager" sort={sort} className={TH} />
              {months.map((m, i) => (
                <SortTh key={m} label={monthLabel(m, 'short')} sortKey={`m${i}`} sort={sort} align="right" className={THR} title={`Score in ${monthLabel(m, 'long')}`} />
              ))}
              <SortTh
                label={`Change · ${monthLabel(months[at], 'short')}`}
                sortKey="delta"
                sort={sort}
                align="right"
                className={`${THR} whitespace-nowrap`}
                title={at === 0 && last > 0
                  ? `${monthLabel(months[at], 'long')} against the month before, which is outside these six months — so the amount is not shown`
                  : `${monthLabel(months[at], 'long')} against the month before`}
              />
              <th className={`${TH} whitespace-nowrap`}>Standing · {monthLabel(months[at], 'short')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {shown.map((e) => {
              const prevBand = at >= 1 ? e.bands[at - 1] : null;
              const curBand = e.bands[at];
              const change = changeAt(e, at);
              return (
                <tr key={e.code}>
                  <td className={TD}>
                    <div className="font-medium text-gray-900">{e.name}</div>
                    <div className="text-[11px] text-gray-400">{e.code}</div>
                  </td>
                  <td className={`${TD} max-w-[200px]`}>
                    <div className="truncate" title={e.store || undefined}>{e.store || '—'}</div>
                    {e.city && <div className="truncate text-[11px] text-gray-400">{e.city}</div>}
                  </td>
                  <td className={`${TD} max-w-[140px] truncate`} title={e.managerName || undefined}>{e.managerName || '—'}</td>
                  {e.totals.map((t, i) => (
                    <td key={months[i]} className={i === at ? tdr('text-gray-900', 'font-medium') : TDR}>{t == null ? '—' : t.toFixed(1)}</td>
                  ))}
                  <td className={tdr(TONE_TEXT[toneOf(change)], 'font-medium')}>{fmtDelta(change)}</td>
                  <td className={`${TD} whitespace-nowrap`}>
                    {prevBand && prevBand !== curBand ? (
                      <span className="inline-flex items-center gap-1"><BandChip band={prevBand} /><span className="text-gray-400">→</span><BandChip band={curBand} /></span>
                    ) : (
                      <BandChip band={curBand} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > MODAL_ROW_CAP && (
        <p className="mt-2 text-center text-[11px] text-gray-500">
          Showing {MODAL_ROW_CAP} of {rows.length.toLocaleString()}. Search to narrow the list, or download it for everyone.
        </p>
      )}
    </>
  );
}

/** A watchlist card's compact rows: who, and last month → this month. */
function WatchRows({ rows, months, showStreak }: { rows: EmployeeMovement[]; months: string[]; showStreak?: boolean }) {
  const last = months.length - 1;
  return (
    <ul className="divide-y divide-gray-50">
      {rows.map((e) => (
        <li key={e.code} className="flex items-center justify-between gap-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-gray-900">{e.name}</div>
            <div className="truncate text-[11px] text-gray-400">{[e.store, e.managerName].filter(Boolean).join(' · ') || e.code}</div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-sm tabular-nums text-gray-700">
              {last >= 1 ? fmtScore(e.totals[last - 1]) : '—'} <span className="text-gray-400">→</span>{' '}
              <span className="font-semibold text-gray-900">{fmtScore(e.totals[last])}</span>
            </div>
            <div className={`text-[11px] font-medium ${TONE_TEXT[toneOf(e.delta)]}`}>
              {fmtDelta(e.delta)}{showStreak ? ` · ${e.declineStreak} months running` : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Breakdown by store attribute ─────────────────────────────────────────────

type Dim = 'trade' | 'category' | 'region' | 'city';
const DIMS: Array<{ key: Dim; label: string }> = [
  { key: 'trade', label: 'Trade' },
  { key: 'category', label: 'Category' },
  { key: 'region', label: 'Region' },
  { key: 'city', label: 'City' },
];
const NO_STORE = '— No store mapped —';
/** Bars shown before "Show all" — a project can have 300 cities. */
const BREAKDOWN_N = 12;

// ── The tab ──────────────────────────────────────────────────────────────────

// The Still falling card's own words, also the heading of its full list.
const STILL_FALLING_TITLE = 'Still falling';
const STILL_FALLING_LINE = `Down ${FALLING_STREAK_MIN} or more months in a row — worth a conversation before the next one.`;
/** Under a list that is not one team's: the people are everyone the filters leave in view. */
const EVERYONE = 'Everyone in view';

type GroupList = 'am' | 'th' | 'manager';
const GROUP_LABEL: Record<GroupList, string> = { am: 'Assistant Manager', th: 'Trade Head', manager: 'Reporting Manager' };

interface Drill {
  title: string;
  subtitle?: string;
  rows: EmployeeMovement[];
  /** Index of the month the list is about. Omitted = the latest month. */
  at?: number;
  /** Present when the drill is one team — adds its own trend and parameter table. */
  group?: GroupSeries;
}

export default function ScoreMovementTab({ projectId, month, roleId, filters }: {
  projectId: number | null;
  month: string;
  roleId?: string;
  filters?: DashRichFilterParams;
}) {
  const [data, setData] = useState<ScoreMovementData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);
  const { regionOf } = useStoreRegions();

  const [chartMode, setChartMode] = useState<ChartMode>('movement');
  const [dim, setDim] = useState<Dim>('trade');
  const [showAllBreakdown, setShowAllBreakdown] = useState(false);
  const [moversBy, setMoversBy] = useState<'am' | 'th'>('am');
  const [drill, setDrill] = useState<Drill | null>(null);
  const [drillSearch, setDrillSearch] = useState('');
  const [fullGroups, setFullGroups] = useState<GroupList | null>(null);
  const [fullSearch, setFullSearch] = useState('');
  // The four lists a headline number opens are named in the address (?list=<slug>)
  // rather than held in `drill`: see `addressDrill`.
  const { list: listSlug, openList, closeList } = useDashboardList();

  useEffect(() => {
    if (projectId == null) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    // A list opened against the old scope must not survive into the new one. (One
    // named in the address is not held here: it is drawn again from the new answer.)
    setDrill(null);
    setDrillSearch('');
    setFullGroups(null);
    scoreMovementDashboardAPI
      .getDashboard(projectId, { month, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== reqRef.current) return;
        setData((res.data?.data ?? null) as ScoreMovementData | null);
      })
      .catch((err) => {
        if (reqId !== reqRef.current) return;
        setError(err.response?.data?.message || 'Failed to load score movement');
        setData(null);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
  }, [month, projectId, roleId, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const months = useMemo(() => data?.months ?? [], [data]);
  const employees = useMemo(() => data?.employees ?? [], [data]);
  const last = months.length - 1;

  const gainers = useMemo(() => topGainers(employees), [employees]);
  const decliners = useMemo(() => topDecliners(employees), [employees]);
  const falling = useMemo(() => stillFalling(employees), [employees]);
  const breakdownRows = useMemo(() => {
    const labelOf = (e: EmployeeMovement): string | undefined =>
      dim === 'trade' ? e.trade : dim === 'category' ? e.category : dim === 'city' ? e.city : e.state ? regionOf(e.state) ?? `${e.state} (no region)` : undefined;
    return breakdownBy(employees, labelOf, NO_STORE);
  }, [employees, dim, regionOf]);

  const monthly = data?.overall.monthly;
  const hasScores = !!monthly && !!monthly[last] && monthly.some((p) => p.scored > 0);
  const anchorLabel = last >= 0 ? monthLabel(months[last], 'long') : '';

  // The people behind three of the headline numbers, for the latest month. The server
  // stamps each person's movement and counts the same people for the tile, so a tile
  // and its list agree; `opensList` below still checks before making one a button.
  const improvedRows = useMemo(() => employees.filter((e) => e.moves[last] === 'improved'), [employees, last]);
  const declinedRows = useMemo(() => employees.filter((e) => e.moves[last] === 'declined'), [employees, last]);
  const scoredRows = useMemo(() => employees.filter((e) => e.totals[last] != null), [employees, last]);

  // The list the address names. DERIVED from it on every render, never copied into
  // `drill`: every refetch clears `drill` (above), and the first one comes a moment
  // after the dashboard opens, when the default Role filter lands. A list opened once
  // on arrival would be shut by that. It shows once scores have arrived.
  const addressList = hasScores ? scoreMovementListFromSlug(listSlug) : null;
  const addressDrill = useMemo((): Drill | null => {
    switch (addressList) {
      case 'still-falling': return { title: STILL_FALLING_TITLE, subtitle: STILL_FALLING_LINE, rows: falling };
      case 'improved': return { title: `${MOVE_META.improved.label} — ${anchorLabel}`, subtitle: EVERYONE, rows: improvedRows, at: last };
      case 'declined': return { title: `${MOVE_META.declined.label} — ${anchorLabel}`, subtitle: EVERYONE, rows: declinedRows, at: last };
      case 'scored': return { title: `Scored in ${anchorLabel}`, rows: scoredRows, at: last };
      default: return null;
    }
  }, [addressList, anchorLabel, falling, improvedRows, declinedRows, scoredRows, last]);
  // A list opened from a chart, a table or a team (page memory) sits over it.
  const shownDrill = drill ?? (addressDrill && addressDrill.rows.length > 0 ? addressDrill : null);

  const drillRows = useMemo(() => {
    const q = drillSearch.trim().toLowerCase();
    const rows = shownDrill?.rows ?? [];
    if (!q) return rows;
    return rows.filter((e) => [e.name, e.code, e.store, e.city, e.managerName, e.amName, e.thName].some((v) => String(v ?? '').toLowerCase().includes(q)));
  }, [shownDrill, drillSearch]);

  if (projectId == null) {
    return <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center text-sm text-gray-500 shadow-sm">Select a project to see score movement.</div>;
  }
  // Whenever a request is in flight, not only on the first one. Keeping the previous
  // answer on screen while the project, month or filters change would show — and let
  // the user download — one scope's people under another scope's selection.
  if (loading) {
    return <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center text-sm text-gray-500 shadow-sm"><InlineLoader label="Loading score movement…" className="flex justify-center" /></div>;
  }
  if (error) {
    return <div className="rounded-xl border border-rose-100 bg-rose-50 py-8 text-center text-sm text-rose-700">{error}</div>;
  }
  if (!data) return null;

  const overall: Series = data.overall;
  const now = overall.monthly[last];
  const prevLabel = last >= 1 ? monthLabel(months[last - 1], 'short') : 'the month before';
  const tiersOn = data.tiers.available;
  const fileStamp = data.anchorMonth.slice(0, 7);

  const openDrill = (d: Drill) => { setDrillSearch(''); setDrill(d); };
  const closeDrill = () => {
    setDrill(null);
    // Emptied on the way out as well: a list opened by the address has no opening
    // step of its own, and a search left behind would show fewer people than its number.
    setDrillSearch('');
    if (scoreMovementListFromSlug(listSlug)) closeList();
  };
  /** A headline number opens its list only when it is above zero and the list holds
   *  exactly that many people. */
  const opensList = (slug: ScoreMovementList, shown: number, rows: EmployeeMovement[]) =>
    (shown > 0 && rows.length === shown ? () => openList(slug) : undefined);
  const membersOf = (list: GroupList, g: GroupSeries): EmployeeMovement[] =>
    employees.filter((e) => ((list === 'am' ? e.amCode : list === 'th' ? e.thCode : e.managerCode) ?? null) === g.key);
  // The headcount on the row is "scored in the latest month"; the list underneath is
  // everyone scored in any of the six. When they differ, say both — a title reading
  // "team of 2" above three rows looks like a miscount.
  const openGroup = (list: GroupList, g: GroupSeries) => {
    const rows = membersOf(list, g);
    const size = rows.length === g.teamSize
      ? `team of ${g.teamSize}`
      : `${g.teamSize} scored in ${anchorLabel} · ${rows.length} in these six months`;
    openDrill({ title: g.name, subtitle: `${GROUP_LABEL[list]} · ${size}`, rows, group: g });
  };

  /** People behind one bar segment of a trend chart, within `pool`. */
  const pickSegment = (pool: EmployeeMovement[], scope: string) => (i: number, key: string, label: string) => {
    const rows = chartMode === 'movement'
      ? pool.filter((e) => e.moves[i] === key)
      : pool.filter((e) => e.totals[i] != null && (e.bands[i] ?? 'other') === key);
    openDrill({ title: `${label} — ${monthLabel(months[i], 'long')}`, subtitle: scope, rows, at: i });
  };
  /** The overall chart. Its latest Improved and Declined bars are the lists the headline
   *  numbers open, so they open the same way: by the address. */
  const pickOverall = (i: number, key: string, label: string) => {
    if (chartMode === 'movement' && i === last && (key === 'improved' || key === 'declined')) openList(key);
    else pickSegment(employees, EVERYONE)(i, key, label);
  };

  const pickTransition = (from: BandKey | 'new', to: BandKey) => {
    const rows = employees.filter((e) => {
      if (e.totals[last] == null || (e.bands[last] ?? 'other') !== to) return false;
      const prevScored = last >= 1 && e.totals[last - 1] != null;
      return from === 'new' ? !prevScored : prevScored && (e.bands[last - 1] ?? 'other') === from;
    });
    openDrill({
      title: `${from === 'new' ? 'Not scored' : BAND_META[from].label} → ${BAND_META[to].label}`,
      subtitle: `${prevLabel} to ${anchorLabel}`,
      rows,
    });
  };

  const download = (name: string, csv: { headers: string[]; rows: string[][] }) =>
    downloadCsv(`${name}-${fileStamp}.csv`, buildCsv(csv.headers, csv.rows));

  const groupRows = (list: GroupList): GroupSeries[] => (list === 'am' ? data.byAm : list === 'th' ? data.byTh : data.byManager);
  const groupCard = (list: GroupList) => {
    const rows = groupRows(list);
    return (
      <Card
        key={list}
        title={`By ${GROUP_LABEL[list]}`}
        subtitle={`${list === 'th' ? "Every team under each Trade Head, combined" : `Each ${GROUP_LABEL[list]}'s team`}, as it stands today — biggest average change first${rows.length > TOP_N ? `, top ${TOP_N} of ${rows.length}` : ''}. Click a row for the team's parameters and people.`}
        action={rows.length > 0 && (
          <ListActions
            onDownload={() => download(`score-movement-by-${list}`, groupCsv(GROUP_LABEL[list], months, rows))}
            onViewAll={rows.length > TOP_N ? () => { setFullSearch(''); setFullGroups(list); } : undefined}
          />
        )}
      >
        {rows.length === 0
          ? <p className="py-6 text-center text-sm text-gray-500">Nobody is scored in this period.</p>
          : <GroupTable rows={rows} months={months} nameLabel={GROUP_LABEL[list]} limit={TOP_N} onRowClick={(g) => openGroup(list, g)} />}
      </Card>
    );
  };

  // `byAddress`: the card's full list is one the address opens (Still falling), so
  // View All opens it there, by address.
  const watchCard = (title: string, subtitle: string, rows: EmployeeMovement[], file: string, empty: string, showStreak = false, byAddress?: ScoreMovementList) => (
    <Card
      title={title}
      subtitle={subtitle}
      action={rows.length > 0 && (
        <ListActions
          onDownload={() => download(file, employeeCsv(months, rows))}
          onViewAll={rows.length > WATCH_N ? () => (byAddress ? openList(byAddress) : openDrill({ title, subtitle, rows })) : undefined}
        />
      )}
    >
      {rows.length === 0
        ? <p className="py-6 text-center text-sm text-gray-500">{empty}</p>
        : <WatchRows rows={rows.slice(0, WATCH_N)} months={months} showStreak={showStreak} />}
    </Card>
  );

  const compared = now ? now.improved + now.declined + now.steady : 0;
  const share = (n: number) => (compared > 0 ? `${((n / compared) * 100).toFixed(0)}% of ${compared.toLocaleString()} compared` : 'nobody to compare');
  const fullList = fullGroups ? groupRows(fullGroups) : [];
  const fullFiltered = fullSearch.trim()
    ? fullList.filter((g) => [g.name, g.key].some((v) => String(v ?? '').toLowerCase().includes(fullSearch.trim().toLowerCase())))
    : fullList;
  const shownBreakdown = showAllBreakdown ? breakdownRows : breakdownRows.slice(0, BREAKDOWN_N);
  const moverGroups = tiersOn ? (moversBy === 'am' ? data.byAm : data.byTh) : data.byManager;
  const moverList: GroupList = tiersOn ? moversBy : 'manager';

  return (
    <div className="space-y-4">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Score movement</h2>
          <p className="text-xs text-gray-500">
            Employee scores month on month{months.length > 0 ? `, ${monthLabel(months[0], 'long')} – ${anchorLabel}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => download('score-movement-employees', employeeCsv(months, employees))}
          disabled={employees.length === 0}
          className={`${PILL} flex-shrink-0 disabled:opacity-50`}
        >
          <Download className="h-3.5 w-3.5" /> Download
        </button>
      </div>

      {/* ─── Why the window ends where it does ─── */}
      {/* The dashboard's month picker defaults to the current month, which on an
          upload-scored project has no scorecard yet. Without this line the tab would
          appear to ignore the month the user is looking at. */}
      {hasScores && data.anchorMonth !== data.requestedMonth && (
        <div className="flex items-start gap-2 rounded-xl border border-sky-100 bg-sky-50 p-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" />
          <p className="text-xs text-sky-900">
            {monthLabel(data.requestedMonth, 'long')} is not scored yet, so this ends at {anchorLabel} — the latest month with scores.
          </p>
        </div>
      )}
      {hasScores && data.provisional && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">
            {anchorLabel} is still in progress. Its scores, and every change measured against them, will keep moving until the month closes.
          </p>
        </div>
      )}
      {data.partial && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">{data.notice || 'Some employee details could not be loaded, so the manager tables may be incomplete.'}</p>
        </div>
      )}

      {!hasScores ? (
        <div className="rounded-2xl border border-gray-200 bg-white py-12 text-center text-sm text-gray-500 shadow-sm">
          No scores are recorded for the selected scope in these six months.
        </div>
      ) : (
        <>
          {/* ─── KPI strip ─── */}
          {/* Seven cards. The first spans two columns below xl, so the 2- and 4-column
              layouts hold eight cells and no row is left one card short.
              Employees scored, Improved and Declined open the people behind them. The
              averages have no list; the line under each is coloured by the direction of
              its change. The two band tiles stay plain: the server counts each person's
              band move but does not send it, so no list here can equal them (Movement
              between bands, below, lists the people move by move). */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
            <StatTile
              label="Employees scored"
              value={now.scored.toLocaleString()}
              helper={anchorLabel}
              className="col-span-2 xl:col-span-1"
              onClick={opensList('scored', now.scored, scoredRows)}
            />
            <StatTile
              label="Average score"
              value={fmtScore(now.avg)}
              helper={now.avgDelta == null ? `no score in ${prevLabel}` : `${fmtDelta(now.avgDelta)} vs ${prevLabel}`}
              helperClassName={TONE_TEXT[toneOf(now.avgDelta)]}
            />
            {/* The middle score. Beside the average on purpose: when a few very low
                scores drag the average, this is the one that still says where the
                typical person stands. */}
            <StatTile
              label="Median score"
              value={fmtScore(now.median)}
              helper={now.median == null
                ? 'not available'
                : now.medianDelta == null ? `no score in ${prevLabel}` : `${fmtDelta(now.medianDelta)} vs ${prevLabel}`}
              helperClassName={TONE_TEXT[toneOf(now.medianDelta)]}
            />
            <StatTile
              label="Improved"
              value={now.improved.toLocaleString()}
              valueTone="good"
              helper={share(now.improved)}
              onClick={opensList('improved', now.improved, improvedRows)}
            />
            <StatTile
              label="Declined"
              value={now.declined.toLocaleString()}
              valueTone={now.declined > 0 ? 'bad' : 'default'}
              helper={share(now.declined)}
              onClick={opensList('declined', now.declined, declinedRows)}
            />
            <StatTile label="Moved up a band" value={now.bandUp.toLocaleString()} valueTone="good" helper={`since ${prevLabel}`} />
            <StatTile label="Moved down a band" value={now.bandDown.toLocaleString()} valueTone={now.bandDown > 0 ? 'bad' : 'default'} helper={`since ${prevLabel}`} />
          </div>

          {/* ─── Trend ─── */}
          <Card
            title="Score trend"
            subtitle={chartMode === 'movement'
              ? `Each person against their own previous month. Improved or declined means a change of ${data.minDelta} point or more. Click a bar for the people.`
              : 'How many people sit in each band, month by month. Click a bar for the people.'}
            action={
              <Segmented
                value={chartMode}
                onChange={setChartMode}
                options={[{ key: 'movement', label: 'Movement' }, { key: 'standing', label: 'Standing' }]}
              />
            }
          >
            <div className="mx-auto w-full max-w-5xl">
              <TrendChart monthly={overall.monthly} mode={chartMode} chartId="score-movement-overall" onPick={pickOverall} />
            </div>
            {chartMode === 'standing' && (
              <p className="mt-3 text-[11px] leading-5 text-gray-500">
                Standing is the Employee Score page's: each person is ranked inside their own manager's team. So every team holds about
                a quarter of its people in each band, and a team's band counts say little about how it compares with another. To compare
                teams, use the average score and the improved / declined counts.
              </p>
            )}
          </Card>

          {/* ─── Band movement + biggest team moves ─── */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card
              title="Movement between bands"
              subtitle={`Where people stood in ${prevLabel} against where they stand in ${anchorLabel}. Click a number for the people.`}
            >
              {data.transitions.length === 0
                ? <p className="py-6 text-center text-sm text-gray-500">Nobody is scored in {anchorLabel}.</p>
                : <TransitionGrid transitions={data.transitions} fromLabel={prevLabel} toLabel={monthLabel(months[last], 'short')} onPick={pickTransition} />}
            </Card>
            <Card
              title="Teams that moved most"
              subtitle={`Average change in score from ${prevLabel} to ${anchorLabel}, for people on the team in both months. Up to ${TEAM_MOVERS_N} each way. Click a team to open it.`}
              action={tiersOn && (
                <Segmented
                  value={moversBy}
                  onChange={setMoversBy}
                  options={[{ key: 'am', label: 'Assistant Manager' }, { key: 'th', label: 'Trade Head' }]}
                />
              )}
            >
              <TeamMoveBars groups={moverGroups} onPick={(g) => openGroup(moverList, g)} />
            </Card>
          </div>

          {/* ─── Parameters ─── */}
          <Card
            title="Parameter trend"
            subtitle="Average score on each parameter as a share of its weight, so a 10-point parameter and a 40-point one read on the same scale."
          >
            {overall.params.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">
                No parameter scores are recorded for these months. They are saved each night from now on, for the current month and the five
                before it. The score trend above is complete.
              </p>
            ) : (
              <>
                <ParameterCards params={overall.params} months={months} minDelta={data.minDelta} />
                {/* States where history STARTS, and promises nothing. The nightly snapshot
                    records the current month and the five before it, so when this window
                    ends earlier than the current month its oldest months are never
                    back-filled — they only come into view as time moves on. */}
                {data.parameterMonths.length < months.length && (
                  <p className="mt-3 text-[11px] text-gray-500">
                    Parameter scores are recorded from {monthLabel(data.parameterMonths[0], 'long')} onward; earlier months in this view have none.
                  </p>
                )}
              </>
            )}
          </Card>

          {/* ─── By manager ─── */}
          {/* Assistant Manager + Trade Head when the project defines those roles (the
              same switch the Training and Mood tabs use), otherwise the plain
              reporting-manager table. */}
          {tiersOn ? <>{groupCard('am')}{groupCard('th')}</> : groupCard('manager')}

          {/* ─── Breakdown ─── */}
          <Card
            title="Score by store"
            subtitle={`Average and median score in ${anchorLabel}, and the average change since ${prevLabel} for people scored in both months, by where they are deployed. Largest groups first.`}
            action={<Segmented value={dim} onChange={(d) => { setDim(d); setShowAllBreakdown(false); }} options={DIMS} />}
          >
            {breakdownRows.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Nobody is scored in {anchorLabel}.</p>
            ) : (
              <>
                {/* Three numbers a row need naming; one did not. */}
                <div className="mb-1 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  <span className="w-44 shrink-0" />
                  <span className="w-10 shrink-0 text-right">People</span>
                  <span className="flex-1" />
                  <span className="w-12 shrink-0 text-right">Avg</span>
                  <span className="w-12 shrink-0 text-right">Median</span>
                  <span className="w-12 shrink-0 text-right">Change</span>
                </div>
                <div className="space-y-2">
                  {shownBreakdown.map((r) => (
                    <div key={r.label} className="flex items-center gap-3">
                      <span className="w-44 shrink-0 truncate text-sm text-gray-700" title={r.label}>{r.label}</span>
                      <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-gray-400" title="People scored">{r.scored.toLocaleString()}</span>
                      <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <span className="block h-full rounded-full bg-primary-500" style={{ width: `${Math.max(Math.min(r.avg ?? 0, 100), 0)}%` }} />
                      </span>
                      <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-900">{fmtScore(r.avg)}</span>
                      <span className="w-12 shrink-0 text-right text-sm font-semibold tabular-nums text-gray-900">{fmtScore(r.median)}</span>
                      <span className={`w-12 shrink-0 text-right text-xs font-medium tabular-nums ${TONE_TEXT[toneOf(r.delta)]}`}>{fmtDelta(r.delta)}</span>
                    </div>
                  ))}
                </div>
                {breakdownRows.length > BREAKDOWN_N && (
                  <button type="button" onClick={() => setShowAllBreakdown((v) => !v)} className="mt-3 text-xs font-medium text-primary-600 hover:underline">
                    {showAllBreakdown ? `Show the largest ${BREAKDOWN_N}` : `Show all ${breakdownRows.length}`}
                  </button>
                )}
              </>
            )}
          </Card>

          {/* ─── Watchlists ─── */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {watchCard('Biggest gains', `Largest rise from ${prevLabel} to ${anchorLabel}.`, gainers, 'score-movement-gains', 'Nobody improved by a point or more.')}
            {watchCard('Sharpest drops', `Largest fall from ${prevLabel} to ${anchorLabel}.`, decliners, 'score-movement-drops', 'Nobody declined by a point or more.')}
            {watchCard(
              STILL_FALLING_TITLE,
              STILL_FALLING_LINE,
              falling,
              'score-movement-still-falling',
              `Nobody has fallen ${FALLING_STREAK_MIN} months running.`,
              true,
              'still-falling',
            )}
          </div>

          {/* ─── Monthly summary ─── */}
          <Card
            title="Monthly summary"
            subtitle="Click a month for everyone scored in it."
            action={<ListActions onDownload={() => download('score-movement-monthly', monthlyCsv(overall))} />}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className={TH}>Month</th>
                    <th className={THR}>Scored</th>
                    <th className={THR}>Avg score</th>
                    {/* NOT "Avg change": the team tables use that for the like-for-like
                        figure (people scored in both months), which can have the opposite
                        sign to this one for the same month. */}
                    <th className={THR} title="This month's average minus last month's. Moves with who joined or left as well as with how people scored.">Change in avg</th>
                    <th className={THR}>Median</th>
                    <th className={THR} title="This month's median minus last month's">Change in median</th>
                    <th className={THR}>Improved</th>
                    <th className={THR}>Declined</th>
                    <th className={THR}>Steady</th>
                    {RANKED_BANDS.map((b) => <th key={b} className={THR}>{BAND_META[b].label}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {[...overall.monthly].reverse().map((p) => {
                    const i = months.indexOf(p.month);
                    return (
                      <tr
                        key={p.month}
                        className={p.scored > 0 ? 'cursor-pointer hover:bg-gray-50' : ''}
                        // The latest month's row is the list "Employees scored" opens:
                        // the same address for both.
                        onClick={p.scored > 0
                          ? () => (i === last
                            ? openList('scored')
                            : openDrill({ title: `Scored in ${monthLabel(p.month, 'long')}`, rows: employees.filter((e) => e.totals[i] != null), at: i }))
                          : undefined}
                      >
                        <td className={`${TD_BASE} font-medium text-gray-900`}>{monthLabel(p.month, 'long')}</td>
                        <td className={TDR}>{p.scored.toLocaleString()}</td>
                        <td className={tdr('text-gray-900', 'font-medium')}>{fmtScore(p.avg)}</td>
                        <td className={tdr(TONE_TEXT[toneOf(p.avgDelta)], 'font-medium')}>{fmtDelta(p.avgDelta)}</td>
                        <td className={tdr('text-gray-900', 'font-medium')}>{fmtScore(p.median)}</td>
                        <td className={tdr(TONE_TEXT[toneOf(p.medianDelta)], 'font-medium')}>{fmtDelta(p.medianDelta)}</td>
                        <td className={TDR}>{p.improved.toLocaleString()}</td>
                        <td className={TDR}>{p.declined.toLocaleString()}</td>
                        <td className={TDR}>{p.steady.toLocaleString()}</td>
                        {RANKED_BANDS.map((b) => <td key={b} className={TDR}>{p.bands[b].toLocaleString()}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── What is and is not in here ─── */}
          <p className="px-1 text-[11px] leading-5 text-gray-500">
            Active employees only. Every month, including earlier ones, is shown under the manager a person reports to today. Scores and
            standing are the Employee Score page's own, read from its nightly snapshot. A month counts for a person only when a ranked score is
            stored for it. A month with no score, a role with no scorecard, a month held out of ranking (such as maternity leave), and a month
            still waiting for its target upload — which Employee Score shows with a provisional score — are all left out rather than counted as
            zero, so this tab can show fewer people for a month than Employee Score does.
            {data.managerRolesExcluded > 0 && (
              <> {data.managerRolesExcluded} {data.managerRolesExcluded === 1 ? 'manager is' : 'managers are'} left out because their score is their
                team's average, not their own — pick their role under Filters to see them on their own.</>
            )}
          </p>
        </>
      )}

      {/* ─── Drill-down: a list of people, or one team ─── */}
      {shownDrill && (
        <Modal title={shownDrill.title} onClose={closeDrill} maxWidth="max-w-6xl">
          <div className="space-y-4">
            {shownDrill.subtitle && <p className="-mt-1 text-xs text-gray-500">{shownDrill.subtitle}</p>}
            {shownDrill.group && (
              <>
                <div className="mx-auto w-full max-w-4xl">
                  <TrendChart
                    monthly={shownDrill.group.monthly}
                    mode={chartMode}
                    chartId="score-movement-team"
                    onPick={pickSegment(shownDrill.rows, shownDrill.group.name)}
                  />
                </div>
                {shownDrill.group.params.length > 0 && data.parameterMonths.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-sm font-semibold text-gray-900">Parameters</h4>
                    <ParameterTable params={shownDrill.group.params} months={months} parameterMonths={data.parameterMonths} minDelta={data.minDelta} />
                  </div>
                )}
              </>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="w-full max-w-sm">
                <SearchBox value={drillSearch} onChange={setDrillSearch} placeholder="Search employee, store or manager" />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">{drillRows.length.toLocaleString()} {drillRows.length === 1 ? 'person' : 'people'}</span>
                <ListActions onDownload={() => download('score-movement-list', employeeCsv(months, drillRows, shownDrill.at))} />
              </div>
            </div>
            <EmployeeTable rows={drillRows} months={months} at={shownDrill.at} />
          </div>
        </Modal>
      )}

      {/* ─── View all: every Assistant Manager / Trade Head ─── */}
      {fullGroups && !shownDrill && (
        <Modal title={`By ${GROUP_LABEL[fullGroups]}`} onClose={() => setFullGroups(null)} maxWidth="max-w-6xl">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="w-full max-w-sm">
                <SearchBox value={fullSearch} onChange={setFullSearch} placeholder={`Search ${GROUP_LABEL[fullGroups].toLowerCase()}`} />
              </div>
              <ListActions onDownload={() => download(`score-movement-by-${fullGroups}`, groupCsv(GROUP_LABEL[fullGroups], months, fullFiltered))} />
            </div>
            {fullFiltered.length === 0
              ? <p className="py-8 text-center text-sm text-gray-500">Nobody matches.</p>
              : <GroupTable rows={fullFiltered} months={months} nameLabel={GROUP_LABEL[fullGroups]} onRowClick={(g) => openGroup(fullGroups, g)} />}
          </div>
        </Modal>
      )}
    </div>
  );
}
